import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MOCK_APONTAMENTO_DIAS } from '../dados-simulados';
import type { StatusApontamentoOs, SubLinhaApontamento } from '../types';

export interface FiltroApontamentos {
  /** Competência — aceita "yyyy-mm" ou "yyyy-mm-dd", só o prefixo importa. */
  mes: string;
  /** Vazio ou ausente = todas as DREs. */
  dreIds?: string[];
  busca?: string;
  status?: StatusApontamentoOs;
  natureza?: 'PF' | 'PJ';
}

/** Grade de apontamentos diários por OS — standalone (Apontamentos) e embutida
 * na aba "Apontamento" do Fechamento Mensal V2. */
export function useApontamentos(filtro: FiltroApontamentos) {
  return useQuery({
    queryKey: ['financeiro', 'apontamentos', filtro],
    queryFn: async () => {
      const competencia = filtro.mes.slice(0, 7);
      let filtrados = MOCK_APONTAMENTO_DIAS.filter(
        (a) => a.mesReferencia.slice(0, 7) === competencia,
      );
      if (filtro.dreIds && filtro.dreIds.length > 0) {
        const idsFiltro = new Set(filtro.dreIds);
        filtrados = filtrados.filter((a) => idsFiltro.has(a.dreId));
      }
      if (filtro.natureza) filtrados = filtrados.filter((a) => a.natureza === filtro.natureza);
      if (filtro.status) filtrados = filtrados.filter((a) => a.status === filtro.status);
      if (filtro.busca) {
        const termo = filtro.busca.toLowerCase();
        filtrados = filtrados.filter(
          (a) =>
            a.credenciadoNome.toLowerCase().includes(termo) || String(a.numeroOs).includes(termo),
        );
      }
      return filtrados;
    },
    enabled: filtro.mes.length > 0,
  });
}

export interface EdicaoCelulaApontamento {
  ordemServicoId: string;
  /** Dia do mês (1–31). */
  dia: number;
  sub: SubLinhaApontamento;
  valor: number | boolean;
}

/** Lança/edita uma célula (dia × sublinha) de uma OS na grade mensal. */
export function useSalvarApontamentoCelula() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (_variaveis: EdicaoCelulaApontamento) => {
      await new Promise((resolve) => setTimeout(resolve, 400));
      return { ok: true as const };
    },
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'apontamentos'] }),
  });
}

/** Aplica o mesmo valor numa sublinha, em vários dias de uma vez ("Aplicar em lote"). */
export function useAplicarLoteCelula() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (_variaveis: {
      ordemServicoId: string;
      dias: number[];
      sub: SubLinhaApontamento;
      valor: number;
    }) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return { ok: true as const };
    },
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'apontamentos'] }),
  });
}

/*
 * Enviar, aprovar e devolver lote NÃO têm gancho aqui, de propósito: ainda não
 * há endpoint, e um gancho que espera 600 ms e devolve `{ ok: true }` fazia a
 * tela fingir que gravou. Essas ações mostram o aviso de indisponibilidade
 * (`ApontamentoGridPage.tsx`; CLAUDE.md, exceção do Fechamento Mensal, item 2).
 * Voltam junto com os endpoints.
 */
